import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from 'tss-react/mui';
import {useParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import MinimalHeader from '../DocumentCollection/components/MinimalHeader';
import {fetchSignPageModel} from './api/signingApi';
import AuthScreen from './screens/AuthScreen';
import SignScreen from './screens/SignScreen';
import CompletedScreen from './screens/CompletedScreen';

const useStyles = makeStyles()((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.background.paper,
    textAlign: 'left',
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    flexDirection: 'column',
    gap: 16,
    padding: '60px 20px',
    textAlign: 'center',
  },
  message: {
    fontSize: theme.typography.pxToRem(16),
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.secondary,
  },
}));

const SigningPage = () => {
  const {classes} = useStyles();
  const {signKey} = useParams();
  const intl = useIntl();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadModel = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setModel(await fetchSignPageModel(signKey));
    } catch (err) {
      setError(err?.message || 'LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, [signKey]);

  useEffect(() => {
    loadModel();
  }, [loadModel]);

  const renderScreen = () => {
    if (loading) {
      return (
        <div className={classes.centered}>
          <CircularProgress size={40} />
          <span className={classes.message}>{intl.formatMessage({id: 'sign.loading'})}</span>
        </div>
      );
    }

    if (error || !model) {
      return (
        <div className={classes.centered}>
          <span className={classes.message}>{intl.formatMessage({id: 'sign.invalidLink'})}</span>
        </div>
      );
    }

    if (model.status === 'SIGNED') {
      return (
        <CompletedScreen
          signKey={signKey}
          signedAt={model.signedAt}
          documentSha256={model.documentSha256}
          documentBase64={model.documentBase64}
        />
      );
    }

    if (model.authStatus === 'AUTH_REQUIRED') {
      return (
        <AuthScreen
          signKey={signKey}
          authMethods={model.authMethods}
          maskedPhone={model.maskedPhone}
          onAuthenticated={loadModel}
        />
      );
    }

    return (
      <SignScreen
        signKey={signKey}
        model={model}
        onSigned={(result) => setModel({...model, ...result, status: 'SIGNED'})}
      />
    );
  };

  return (
    <div className={classes.root} dir='ltr'>
      <MinimalHeader />
      {renderScreen()}
    </div>
  );
};

export default SigningPage;
